import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { ArrowUpRightIcon } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

interface DocumentActionButtonsProps {
  /**
   * Fires the resubmit mutation for the selected document with the agent's
   * (possibly edited) note. The backend posts the note to the SkySlope
   * checklist item — signed `– {agent} via Indigo` — and sets the item back to
   * Pending. The page binds the token/folder/document around this.
   */
  onResubmit: (message: string) => void | Promise<void>;
  /**
   * The note the resubmit textarea is prepopulated with each time the modal
   * opens — the messages of the suggestions the agent has crossed out
   * (ignored), one per line. Empty string when nothing has been crossed out.
   */
  defaultResubmitMessage: string;
  /**
   * SkySlope (or other platform) deep link for the document. When null the
   * SkySlope button is hidden — there is nowhere to send the agent.
   */
  platformUrl?: string | null;
  /** Records the explicit hop before the agent continues in the source platform. */
  onOpenPlatform: () => void;
  /**
   * True when the selected document needs review (open suggestions, not yet
   * resubmitted). Drives the "Ignore and resubmit" action and the "Fix in
   * SkySlope" vs "Open in SkySlope" label.
   */
  needsReview: boolean;
  /** True once the document has already been resubmitted (disables the action). */
  isResubmitted: boolean;
  /** True while the resubmit mutation is in flight. */
  isSubmitting: boolean;
  /**
   * True once the per-document detail (validation id + open suggestions) has
   * loaded. The resubmit trigger is disabled until then so the modal can't be
   * opened — and an incomplete ignore payload sent — before the data it needs
   * exists. Defaults to true so callers that don't depend on async detail (and
   * tests) keep the action enabled. Does NOT affect the SkySlope link label,
   * which is overview-derived.
   */
  isResubmitReady?: boolean;
  /** Detail-load failure that blocks resubmit until the user retries. */
  resubmitLoadError?: Error | null;
  /** Retries loading every document needed to construct the resubmit payload. */
  onRetryResubmitLoad?: () => void;
}

/**
 * Document-level action buttons shown above the suggestions for the selected
 * document.
 *
 * - **Ignore and resubmit / Resubmitted**: shown for a needs-review document
 *   (opens the resubmit modal) or a already-resubmitted one (disabled
 *   "Resubmitted"). Hidden for clean, never-resubmitted documents.
 * - **Fix in SkySlope / Open in SkySlope**: opens the platform deep link in a
 *   new tab. Labeled "Fix in SkySlope" for needs-review documents and "Open in
 *   SkySlope" otherwise. Hidden when no `platformUrl` is available.
 *
 * Renders nothing when neither a resubmit action nor a platform link applies.
 */
export const DocumentActionButtons: FC<DocumentActionButtonsProps> = ({
  onResubmit,
  defaultResubmitMessage,
  platformUrl,
  onOpenPlatform,
  needsReview,
  isResubmitted,
  isSubmitting,
  isResubmitReady = true,
  resubmitLoadError = null,
  onRetryResubmitLoad,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [message, setMessage] = useState(defaultResubmitMessage);

  // Reset the editable note to the prepopulated default each time the modal
  // opens, so reopening always starts from the current crossed-out suggestions
  // (and discards any abandoned edits from a prior open).
  useEffect(() => {
    if (isOpen) {
      setMessage(defaultResubmitMessage);
    }
  }, [isOpen, defaultResubmitMessage]);

  const isEmpty = message.trim().length === 0;

  const handleResubmit = async (): Promise<void> => {
    if (isEmpty) {
      return;
    }
    // Only close the modal AFTER the resubmit resolves — if the mutation fails
    // the modal stays open with the edited note intact, instead of appearing to
    // succeed and discarding the note.
    await onResubmit(message);
    onClose();
  };

  // The resubmit button shows for a needs-review document (actionable) or an
  // already-resubmitted one (disabled "Resubmitted"). A clean, never-resubmitted
  // document gets no resubmit button — only the platform link.
  const showResubmit = needsReview || isResubmitted;

  if (!showResubmit && platformUrl == null) {
    return null;
  }

  return (
    <>
      <Flex align="flex-end" direction="column" flexShrink={0} gap={2}>
        {resubmitLoadError != null && onRetryResubmitLoad != null && (
          <Flex align="center" gap={2}>
            <Text color="compliance.notification.error.text" fontSize="xs">
              Couldn&apos;t load all documents needed to resubmit.
            </Text>
            <Button
              colorScheme="red"
              size="xs"
              variant="outline"
              onClick={onRetryResubmitLoad}
            >
              Retry
            </Button>
          </Flex>
        )}
        <Flex gap={2}>
          {showResubmit && (
            <Button
              _hover={{ bg: 'whiteAlpha.200' }}
              bg="whiteAlpha.100"
              borderColor="whiteAlpha.200"
              borderRadius="8px"
              borderWidth={1}
              color="white"
              fontSize="sm"
              fontWeight="medium"
              isDisabled={isResubmitted || !isResubmitReady}
              isLoading={isSubmitting}
              size="sm"
              onClick={onOpen}
            >
              {isResubmitted ? 'Resubmitted' : 'Ignore and resubmit'}
            </Button>
          )}
          {platformUrl != null && (
            <Button
              _hover={{ bg: 'cyan.400' }}
              bg="cyan.500"
              borderRadius="8px"
              color="indigo.900"
              fontSize="sm"
              fontWeight="medium"
              rightIcon={<ArrowUpRightIcon size={14} />}
              size="sm"
              onClick={() => {
                window.open(platformUrl, '_blank', 'noopener,noreferrer');
                onOpenPlatform();
              }}
            >
              {needsReview ? 'Fix in SkySlope' : 'Open in SkySlope'}
            </Button>
          )}
        </Flex>
      </Flex>

      <Modal isCentered isOpen={isOpen} size="xl" onClose={onClose}>
        <ModalOverlay />
        <ModalContent
          bg="compliance.background.primary"
          borderColor="compliance.border.primary"
          borderWidth={1}
        >
          <ModalHeader
            color="compliance.text.primary"
            fontSize="lg"
            fontWeight={600}
          >
            Resubmit to reviewer
          </ModalHeader>
          <ModalCloseButton color="compliance.text.secondary" />
          <ModalBody>
            <Text color="compliance.text.secondary" fontSize="sm" mb={3}>
              This note will be added to the SkySlope checklist item and sent
              back for review.
            </Text>
            <Textarea
              _focus={{ borderColor: 'compliance.input.focus.border' }}
              _placeholder={{ color: 'compliance.input.placeholder' }}
              aria-label="Resubmit note"
              bg="compliance.input.bg"
              borderColor="compliance.input.border"
              color="compliance.input.text"
              fontSize="sm"
              minH="280px"
              placeholder="Add a note for the reviewer…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </ModalBody>
          <ModalFooter gap={3}>
            <Button
              _hover={{ bg: 'compliance.button.secondary.hover' }}
              bg="compliance.button.secondary.bg"
              color="compliance.button.secondary.text"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              _hover={{ bg: 'compliance.button.primary.hover' }}
              bg="compliance.button.primary.bg"
              color="compliance.button.primary.text"
              isDisabled={isEmpty}
              isLoading={isSubmitting}
              onClick={() => {
                void handleResubmit();
              }}
            >
              Resubmit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
